import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import ActionsGrid from 'components/ActionsGrid'
import ZoneModal from 'components/ZoneModal'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { selectIsLoadingZones, selectAvailableZones, setIsInitialized, addAlert, fetchZones, selectAllGroups, fetchGroups } from 'store/global'
import { selectDisplayedZone, setDisplayedZone, closeDisplayedZone, submitZone, deleteZone } from 'store/organizations'
import messages from './messages'
import styles from './Zones.module.scss'
import Input from 'components/Input'
import { orderBy } from 'lodash'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import { saveAs } from 'file-saver'
import { Parser } from 'json2csv'
import Loader from 'components/Loader/Loader'
import { usePermissions } from 'hooks/usePermissions'

import { gridColumns } from './specs'

export default function Zones() {
  const dispatch = useDispatch()
  const permissions = usePermissions()
  const isLoading = useSelector(selectIsLoadingZones)
  const allZones = useSelector(selectAvailableZones)
  const displayedZone = useSelector(selectDisplayedZone)
  const allGroups = useSelector(selectAllGroups)

  const [sortColumn, setSortColumn] = useState(null)
  const [sortOrder, setSortOrder] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')

  const sortableColumns = new Set(['id', 'name', 'groups'])

  useEffect(() => {
    dispatch(closeDisplayedZone())
    dispatch(setIsInitialized(true))
    dispatch(fetchZones())
  }, [])

  useEffect(() => {
    if (!allGroups || allGroups.length === 0) {
      dispatch(fetchGroups())
    }
  }, [dispatch, allGroups])

  const onSort = column => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const filteredZones = React.useMemo(() => {
    if (!allZones || !Array.isArray(allZones)) return []
    if (!searchTerm) return allZones
    return allZones.filter(zone =>
      Object.values(zone).some(value => value?.toString().toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }, [allZones, searchTerm])

  const sortedZones = React.useMemo(() => {
    if (!sortColumn) return filteredZones
    return orderBy(filteredZones, [sortColumn], [sortOrder])
  }, [filteredZones, sortColumn, sortOrder])

  const onDisplayModal = (action = 'create', existingZone = {}) => {
    const newZone = {
      ...existingZone,
      action,
      isOpen: true,
    }

    dispatch(setDisplayedZone({ zone: newZone }))
  }

  const flattenGroupsForOptions = (groups, level = 0) => {
    let allGroups = []
    groups.forEach(group => {
      allGroups.push({
        value: group.id,
        label: group.path,
        path: group.path,
      })
      if (group.subGroups && group.subGroups.length > 0) {
        allGroups = allGroups.concat(flattenGroupsForOptions(group.subGroups, level + 1))
      }
    })
    return allGroups
  }

  const onSubmit = async zone => {
    try {
      if (displayedZone.action === 'delete') {
        await dispatch(deleteZone({ zoneId: displayedZone.id }))
      } else {
        await dispatch(submitZone({ zone }))
      }

      dispatch(addAlert({ type: 'success', message: 'Zone Action Performed' }))
      dispatch(setDisplayedZone())
    } catch (error) {
      dispatch(addAlert({ type: 'error', message: 'Zone Action Failed' }))
    }
  }

  const onExportCSV = () => {
    if (!sortedZones.length) return
    const fields = Object.keys(sortedZones[0])
    const parser = new Parser({ fields })
    const csv = parser.parse(sortedZones)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    saveAs(blob, 'zones.csv')
  }

  const renderGridCell = (zone, column) => {
    if (column === 'actions') {
      return (
        <div className={styles.actions}>
          <Button color='primary' outline onClick={() => onDisplayModal('edit', zone)}>
            <Icon name='edit' size='regular'>
              <FormattedMessage id="app.containers.Zones.actions.edit" defaultMessage="Edit" />
            </Icon>
          </Button>
          <Button color='danger' outline onClick={() => onDisplayModal('delete', zone)}>
            <Icon name='delete' size='regular'>
              <FormattedMessage id="app.containers.Zones.actions.delete" defaultMessage="Delete" />
            </Icon>
          </Button>
        </div>
      )
    }
    
    if (column === 'groups') {
      const groups = zone.groups || []
      if (groups.length === 0) return ''
      
      if (!allGroups) return ''
      const flattenedGroups = flattenGroupsForOptions(allGroups)
      const availableGroups = groups.filter(group => {
        return flattenedGroups.some(flattenedGroup => 
          flattenedGroup.path === group
        )
      })
      return availableGroups.join(', ')
    }
    
    return zone[column]
  }

  if (isLoading || !allGroups) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderContainer}>
          <Loader type='globalLoading' message='Loading zones and groups...' />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <Input
        placeholder='Input search text...'
        value={searchTerm}
        onChange={value => setSearchTerm(value)}
        className={styles.search}
      />

      <ActionsGrid
        isLoading={isLoading}
        type='zone'
        rows={sortedZones}
        columns={gridColumns}
        header={<FormattedMessage {...messages.header} />}
        onAction={onDisplayModal}
        onSort={onSort}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onExportCSV={onExportCSV}
        showCreateButton={permissions.admin}
      >
        {(zone, columnKey, isHeader) =>
          isHeader ? (
            <div>
              {sortableColumns.has(columnKey) ? (
                <div className={styles.header} onClick={() => onSort(columnKey)}>
                  <FormattedMessage {...messages.columns[columnKey]} /> <SwapVertIcon fontSize='small' />
                </div>
              ) : (
                <FormattedMessage {...messages.columns[columnKey]} />
              )}
            </div>
          ) : (
            renderGridCell(zone, columnKey)
          )
        }
      </ActionsGrid>
      {displayedZone.isOpen && <ZoneModal mode={displayedZone.action} onSubmit={onSubmit} />}
    </div>
  )
}
